/** 成交 —— 逐笔，是「对账平」这句结论的最小单位。
 *
 * 规格 `docs/design/screens/fills.md`。`slippage_cost` 在账本里是 `@property`，后端显式带出；
 * 某行缺这个字段时显示「缺」并且 KPI 不合计——「滑点为 0」与「滑点没给」是两回事，不按 0 计。
 * 对账列取 `GET /reconciliations/{run_id}` 的真实结果，没有结果显示「未对账」，不显示「平」。
 */

import { useFillsBook, useOrdersBook, useRecon, useRunTrace } from "../../api/g2";
import { useStatus } from "../../api/queries";
import { useSimulationConfig } from "../../lib/g1Queries";
import { Card, Kpi, Lede, PageHead } from "../../components/Card";
import { Pill, SidePill } from "../../components/Pill";
import { QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { DASH, money, num, short, ts } from "../../lib/format";
import { BookSelector, InlineError, Missing, useBookParams } from "../orders/Book";

export function FillsScreen(): React.ReactNode {
  const [book] = useBookParams();
  const status = useStatus();
  const fills = useFillsBook(book);
  const orders = useOrdersBook(book);
  const runId = fills.data?.run_id ?? null;
  const run = useRunTrace(runId);
  const recon = useRecon(runId);

  if (fills.isPending) return <QueryState status="pending" error={null} what="成交">{null}</QueryState>;
  if (fills.isError) {
    return (
      <>
        <PageHead title="成交" actions={<BookSelector envelope={null} />} />
        <QueryState status="error" error={fills.error} what="成交（GET /api/v1/fills）" refetch={() => void fills.refetch()}>
          {null}
        </QueryState>
      </>
    );
  }

  const trades = fills.data.rows;
  const venue = new Map((orders.data?.rows ?? []).map((order) => [order.client_order_id, order.venue_order_id]));
  const commission = trades.reduce((sum, trade) => sum + trade.commission, 0);
  const slipMissing = trades.some((trade) => trade.slippage_cost === null);
  const slippage = trades.reduce((sum, trade) => sum + (trade.slippage_cost ?? 0), 0);
  const neverRan = !book.run_id && status.isSuccess && status.data.current_run === null;

  // 运行归档里的合计是 KPI 的准数；逐笔加总只作核对，不一致标黄。
  const archivedSlip = run.data?.slippage_cost ?? null;
  const archivedFee = run.data?.commissions ?? null;
  const slipMismatch = archivedSlip !== null && !slipMissing && Math.abs(archivedSlip - slippage) > 1e-6;
  const feeMismatch = archivedFee !== null && Math.abs(archivedFee - commission) > 1e-6;

  const reconBy = (tradeId: string): React.ReactNode => {
    if (!runId) return <span className="faint">未对账</span>;
    if (recon.isError) return <span className="faint" title={String(recon.error)}>未对账</span>;
    if (!recon.data) return <span className="faint">…</span>;
    if (!recon.data.checked) return <span className="faint">未对账</span>;
    if (recon.data.missing_on_channel.includes(tradeId)) return <Pill tone="bad">本地有通道无</Pill>;
    if (recon.data.mismatched.includes(tradeId)) return <Pill tone="bad">数量价格不一致</Pill>;
    return <Pill tone="ok">平</Pill>;
  };
  const reconKpi = !runId || recon.isError || !recon.data?.checked
    ? <span className="faint">还没对过账</span>
    : recon.data.balanced
      ? <Pill tone="ok">平</Pill>
      : <Pill tone="bad">不平</Pill>;

  return (
    <>
      <PageHead title="成交" sub={<>{trades.length} 笔</>} actions={<BookSelector envelope={fills.data} />} />
      <Lede>
        撮合价是 bar 上的价，<b>含滑点价</b>是按配置 bps 换算到账上看得见的成交价——两者之差就是滑点成本。
        滑点与手续费都是<b>成本项（已含在现金里，权益口径总盈亏已扣）</b>，本屏不另给盈亏数。
      </Lede>

      <div className="grid g4">
        <Kpi label="成交笔数" value={num(trades.length, 0)} detail="GET /fills · 本地账" />
        <Kpi
          label="手续费合计（成本项）"
          value={<span className={feeMismatch ? "warn" : undefined}>{money(archivedFee ?? commission)}</span>}
          detail={
            archivedFee === null
              ? "逐笔加总（运行归档未给合计）"
              : feeMismatch
                ? `运行归档合计；逐笔加总 ${money(commission)} 不一致`
                : "运行归档合计，与逐笔加总一致"
          }
        />
        <Kpi
          label="滑点成本（成本项）"
          value={
            slipMissing ? (
              <span className="faint">未合计</span>
            ) : (
              <span className={slipMismatch ? "warn" : undefined}>{money(archivedSlip ?? slippage)}</span>
            )
          }
          detail={
            slipMissing
              ? "接口未给滑点，未合计"
              : archivedSlip === null
                ? "逐笔加总（运行归档未给合计）"
                : slipMismatch
                  ? `运行归档合计；逐笔加总 ${money(slippage)} 不一致`
                  : "运行归档合计，与逐笔加总一致"
          }
        />
        <Kpi
          label="对账"
          value={reconKpi}
          detail={recon.data?.summary ?? (runId ? `GET /reconciliations/${runId}` : "当前账未绑定运行")}
        />
      </div>

      {recon.data && recon.data.missing_locally.length > 0 ? (
        <div className="note bad" style={{ marginTop: 12 }}>
          通道有本地无：{recon.data.missing_locally.join("、")}（本表没有这些行，<a href="#reconciliation">去对账屏</a>）
        </div>
      ) : null}
      {orders.isError ? (
        <div style={{ marginTop: 12 }}>
          <InlineError endpoint="GET /api/v1/orders（券商单号列）" error={orders.error} onRetry={() => void orders.refetch()} />
        </div>
      ) : null}

      <div style={{ marginTop: 12 }}>
        <Card nopad>
          <Table
            cols={[
              { label: "trade_id", mono: true },
              { label: "时间（UTC）", mono: true },
              { label: "标的" },
              { label: "方向" },
              { label: "数量", num: true },
              { label: "撮合价", num: true },
              { label: "含滑点价", num: true },
              { label: "滑点（成本项）", num: true },
              { label: "手续费（成本项）", num: true },
              { label: "订单", mono: true },
              { label: "券商单号", mono: true },
              { label: "仓位", mono: true },
              { label: "对账" },
            ]}
            rows={trades.map((trade) => {
              const bad = recon.data?.missing_on_channel.includes(trade.trade_id) || recon.data?.mismatched.includes(trade.trade_id);
              return {
                key: trade.trade_id,
                cls: bad ? "warnrow" : "",
                cells: [
                  <span className="mono">{trade.trade_id}</span>,
                  ts(trade.ts_event),
                  trade.instrument_id || DASH,
                  <SidePill side={trade.side} />,
                  num(trade.last_qty, 0),
                  num(trade.last_px, 4),
                  num(trade.slipped_px, 4),
                  trade.slippage_cost === null ? <Missing /> : <span className={trade.slippage_cost > 0 ? "neg" : "dim"}>{num(trade.slippage_cost, 2)}</span>,
                  money(trade.commission),
                  <a className="mono" href={`#orders?id=${encodeURIComponent(trade.client_order_id)}`}>{short(trade.client_order_id, 20)}</a>,
                  orders.isError ? <span className="faint">未取到</span> : venue.get(trade.client_order_id) ?? <span className="faint">—</span>,
                  trade.position_id ? <a className="mono" href="#positions">{trade.position_id}</a> : <span className="faint">—</span>,
                  reconBy(trade.trade_id),
                ],
              };
            })}
            empty={neverRan ? "还没有任何运行，本地账没有成交" : <EmptyFills orders={orders.data?.rows ?? null} />}
            tall
          />
        </Card>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>
        配置指纹：<span className="mono">{run.data?.config_digest ?? status.data?.config_digest ?? "—"}</span>。
        <SimulationHint />
      </div>
    </>
  );
}

function EmptyFills({ orders }: { orders: { status: string }[] | null }): React.ReactNode {
  if (!orders) return "该运行没有成交";
  const rejected = orders.filter((order) => order.status === "REJECTED").length;
  return (
    <>
      该运行没有成交（订单 {orders.length} 笔，被拒 {rejected} 笔，见<a href="#orders">订单屏</a>）
    </>
  );
}

/** 页尾撮合假设：只引 `body.slippage.bps` 与 `body.fee.model`，全文在设置屏（规格 fills.md）。 */
function SimulationHint(): React.ReactNode {
  const sim = useSimulationConfig();
  if (sim.status !== "success") {
    return <>撮合假设明细见设置屏（{sim.status === "error" ? "GET /config/simulation 取不到" : "正在取 GET /config/simulation"}）。</>;
  }
  const body = sim.data.body as Record<string, unknown>;
  const pick = (key: string, sub: string): string => {
    const node = body[key];
    const v = node && typeof node === "object" ? (node as Record<string, unknown>)[sub] : undefined;
    return typeof v === "string" || typeof v === "number" ? String(v) : "—";
  };
  return (
    <>
      撮合假设：滑点 {pick("slippage", "bps")} bps · 手续费模型 {pick("fee", "model")}（全文见设置屏，<code>GET /config/simulation</code>）。
    </>
  );
}
